/**
 * Shared dark-themed hero scaffolding for Solution pages.
 *
 * Provides the gradient background, grid texture, container, badge pill,
 * and large headline. Everything below the headline (subtitle, callout,
 * CTAs, slogan, etc.) goes in `children` so each page keeps full freedom.
 *
 * Props:
 *  - badge:       { icon: LucideIcon, label: string }
 *  - headline:    ReactNode  (use a <span className="gradient-text"> for accents)
 *  - align:       "left" | "center"   (default "center")
 *  - maxWidth:    Tailwind size key — "5xl" | "6xl"  (default "5xl")
 *  - minHeight:   Tailwind value — "85vh" | "90vh"   (default "85vh")
 *  - accentColor: hex string used for the soft background glow (default gold)
 *  - children:    everything rendered below the headline inside the reveal block
 *  - testId:      optional data-testid
 */

const MAX_W = {
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
};

const MIN_H = {
  "85vh": "min-h-[85vh]",
  "90vh": "min-h-[90vh]",
};

const PolicyHero = ({
  badge,
  headline,
  align = "center",
  maxWidth = "5xl",
  minHeight = "85vh",
  accentColor = "#D4AF37",
  testId,
  children,
}) => {
  const Icon = badge?.icon;
  const isCenter = align === "center";

  return (
    <section
      data-testid={testId}
      className={`relative ${MIN_H[minHeight] || MIN_H["85vh"]} flex items-center overflow-hidden pt-16 md:pt-20`}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] via-[#0F2251] to-[#0B1A3B]" />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] rounded-full blur-[120px]"
        style={{ backgroundColor: `${accentColor}0D` /* ~5% alpha */ }}
      />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(${accentColor} 1px, transparent 1px), linear-gradient(90deg, ${accentColor} 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />

      <div className={`${MAX_W[maxWidth] || MAX_W["5xl"]} mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10 w-full`}>
        <div className={`reveal ${isCenter ? "text-center" : ""}`}>
          {badge && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 bg-white/[0.06] border border-white/10 backdrop-blur-md text-xs font-bold uppercase tracking-[0.15em] text-white/70 mb-8 rounded-full`}>
              {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" />}
              {badge.label}
            </div>
          )}

          {headline && (
            <h1
              className={`text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white mb-6 leading-[1.05] ${
                isCenter ? "mx-auto max-w-4xl" : "max-w-5xl"
              }`}
            >
              {headline}
            </h1>
          )}

          {children}
        </div>
      </div>
    </section>
  );
};

export default PolicyHero;
